import CustomAutoComplete from "@/modules/common/components/input/dropdowns/auto-complete";
import EditFieldModal from "@/modules/common/components/modals/edit-field-modal";
import useTrackTypesFilterOptions from "@/modules/common/hooks/lookups/track-types-filter-options.hook";
import { Box } from "@mui/material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { addTrackToPropertySchema } from "../../utils";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAddTrackToProperty } from "../../hooks/add-track-to-property.hook";
import { showToast } from "@/modules/common/utils";
import { TOAST_IDS, ToastType } from "@/modules/common/types";

type AddTrackToPropertyFormFields = z.infer<
  ReturnType<typeof addTrackToPropertySchema>
>;

export default function AddTrackForm({
  propertyUId,
  modalOpen,
  setModalOpen,
}: {
  propertyUId: string;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
}) {
  const { data: trackTypes, isLoading: TrackTypesLoading } =
    useTrackTypesFilterOptions();

  const { handleSubmit, reset, control } =
    useForm<AddTrackToPropertyFormFields>({
      resolver: zodResolver(addTrackToPropertySchema()),
    });

  const onSubmit: SubmitHandler<AddTrackToPropertyFormFields> = async (
    data
  ) => {
    submitTrackType(data);
  };

  const mutationAddTrackToProperty = useAddTrackToProperty();

  const submitTrackType = (data: AddTrackToPropertyFormFields) => {
    mutationAddTrackToProperty.mutate(
      {
        uid: propertyUId,
        data: { trackTypeId: data.trackType.value },
      },

      {
        onSuccess: () => {
          handleCloseModal();
          showToast({
            type: ToastType.SUCCESS,
            message: `Property Track added successfully.`,
            toastId: TOAST_IDS.ADD_TRACK_SUCCESS,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
        onError: (error) => {
          showToast({
            type: ToastType.ERROR,
            message:
              error?.response?.data?.message ||
              "Unexpected error occurred. Please try again.",
            toastId: TOAST_IDS.ADD_TRACK_ERROR,
            options: {
              position: "bottom-left",
              autoClose: 3000,
            },
          });
        },
      }
    );
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    reset();
  };
  return (
    <EditFieldModal
      open={modalOpen}
      setOpen={setModalOpen}
      header="Add New Track"
      width={480}
      handleConfirm={handleSubmit(onSubmit)}
      handleCloseModal={handleCloseModal}
      isLoading={mutationAddTrackToProperty.isPending}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.nawy.spacing.md,
        })}
      >
        <Controller
          control={control}
          name="trackType"
          render={({ field, fieldState }) => (
            <CustomAutoComplete
              options={
                trackTypes?.map((type) => ({
                  label: type.name,
                  value: type.ids[0]?.toString() || "",
                })) || []
              }
              required
              loading={TrackTypesLoading}
              placeholder="Property Track"
              label="Property Track"
              onChange={(value) => field.onChange(value)}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            ></CustomAutoComplete>
          )}
        ></Controller>
      </Box>
    </EditFieldModal>
  );
}
